import React, { Component, memo, useMemo } from 'react';
import useCallProvider from '../../hooks/calls/useCallProvider';
import useCallsBalance from '../../hooks/calls/useCallsBalance';
import useVoiceCallsDiagnostics from '../../hooks/calls/useVoiceCallsDiagnostics';
import { WhatsappCallsRuntimeProvider } from '../../hooks/calls/WhatsappCallsRuntimeContext';
import createCallUiEvents from '../../hooks/calls/callUiEvents';
import WhatsappCallManagerView from '../../hooks/calls/WhatsappCallManagerView';
import useFonemaWhatsappAudio from '../../hooks/fonema/useFonemaWhatsappAudio';
import useFonemaWhatsappAudioData from '../../hooks/fonema/useFonemaWhatsappAudioData';
import { VoiceRuntimeBoot } from '../../hooks/voice';
import useVoiceWhatsappAudioBridge from '../../hooks/voice/application/useVoiceWhatsappAudioBridge';

class CallManagerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      '[VoiceSocket] call manager UI crashed — socket/WebRTC boot continues',
      error,
      errorInfo?.componentStack
    );
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const FonemaRuntimeCallManager = memo(() => {
  const data = useFonemaWhatsappAudioData();
  const audio = useFonemaWhatsappAudio({
    ...data,
    canReceiveCalls: data.canReceiveCalls
  });

  return (
    <WhatsappCallsRuntimeProvider provider="fonema">
      <WhatsappCallManagerView data={data} audio={audio} />
    </WhatsappCallsRuntimeProvider>
  );
});

const VoiceProviderCallManager = memo(() => {
  const callUiEvents = useMemo(() => createCallUiEvents(), []);
  const audio = useVoiceWhatsappAudioBridge({ callUiEvents });
  const data = { from: audio.from };

  return (
    <>
      <VoiceRuntimeBoot />
      <WhatsappCallsRuntimeProvider provider="voice" callUiEvents={callUiEvents}>
        <WhatsappCallManagerView data={data} audio={audio} />
      </WhatsappCallsRuntimeProvider>
    </>
  );
});

FonemaRuntimeCallManager.displayName = 'FonemaRuntimeCallManager';
VoiceProviderCallManager.displayName = 'VoiceProviderCallManager';

const CallManager = memo(() => {
  const provider = useCallProvider();
  const hasCallsBalance = useCallsBalance();
  useVoiceCallsDiagnostics(provider, { callsBalanceOk: hasCallsBalance });

  if (provider !== 'voice' && provider !== 'fonema') {
    return null;
  }

  if (!hasCallsBalance) {
    return null;
  }

  return (
    <CallManagerErrorBoundary>
      {provider === 'voice' ? <VoiceProviderCallManager /> : <FonemaRuntimeCallManager />}
    </CallManagerErrorBoundary>
  );
});

CallManager.displayName = 'CallManager';

export default CallManager;
